import React, { useState, PropsWithChildren, useEffect } from 'react';
import { StyledWrapper } from './DataGrid';
import { TextInput } from '../TextInput';
import { Button } from '../Button';
import styled from 'styled-components';
import { IBootstrapTableColumnProps } from 'react-bootstrap-table-next';
import { IconType } from '../Icon';
import { NumberInput } from '../NumberInput';
import { DateRangeFilter } from './DateRangeFilter';
import { ISelectOption } from '../Select';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { SelectFilter } from './SelectFilter';
import { mergeTests } from '../../test-utilities/mergeTests';

interface IFilterBox {
  width?: string;
}

const FilterBox = styled.td<IFilterBox>`
  ${(props) => (props.width ? `width: ${props.width};` : '')}
`;

export interface IFilterValues {
  [key: string]: {
    comparator: string;
    filterType: string;
    filterVal: any;
  };
}
export interface IFilterValue {
  dataField: string;
  value: any;
}

export enum eFilterType {
  Text = 'Text',
  Number = 'Number',
  DateRange = 'Date Range',
  Currency = 'Currency',
  Select = 'Select',
}

export interface IFilteredDataGridColumnProps<T extends object = {}> extends IBootstrapTableColumnProps<T> {
  filterDefault?: any;
  filterElement?: eFilterType;
  filterPlaceholder?: string;
  filterOptions?: ISelectOption[];
}

export interface IMultiPieceSearchBarProps<T extends object = {}> extends IHasTestData {
  onSearch: (filterValues: IFilterValues) => void;
  columns: IFilteredDataGridColumnProps<T>[];
}

export const initializeFiltersState = <T extends object = {}>(
  columns: IFilteredDataGridColumnProps<T>[]
): IFilterValues =>
  columns.reduce(
    (p, c) => ({ ...p, [c.dataField]: { comparator: '', filterType: '', filterVal: c.filterDefault } }),
    {}
  );

const columnWidth = <T extends object = {}>(column: IFilteredDataGridColumnProps<T>, index: number) => {
  const value =
    column.headerStyle &&
    (typeof column.headerStyle === 'function' ? column.headerStyle(column, index).width : column.headerStyle.width);
  return typeof value === 'number' ? `${value}px` : value;
};

export const MultiPieceSearchBar = <T extends object = {}>({
  onSearch,
  columns,
  'data-test-id': dataTestId,
  'data-test-class': dataTestClass,
}: PropsWithChildren<IMultiPieceSearchBarProps<T>>) => {
  const [currentValues, setCurrentValues] = useState<IFilterValues>(initializeFiltersState(columns));

  useEffect(() => {
    const newFilters = initializeFiltersState(columns);
    setCurrentValues(newFilters);
  }, [columns]);

  const updateCurrentValues = (newFilterValue: IFilterValue) => {
    // Since we always create a new object, we want to see if we need to update the data.
    if (
      !currentValues[newFilterValue.dataField] ||
      currentValues[newFilterValue.dataField].filterVal !== newFilterValue.value
    ) {
      setCurrentValues({
        ...currentValues,
        [newFilterValue.dataField]: { comparator: '', filterType: '', filterVal: newFilterValue.value },
      });
    }
  };

  const displayColumns = columns.map((t) => ({ field: t, data: currentValues[t.dataField] }));

  const lastColumnIndex = columns.length - 1;
  const lastColumn = columns[lastColumnIndex];

  return displayColumns.length === 0 ? null : (
    <StyledWrapper>
      <form
        data-test-class={mergeTests(dataTestClass || 'data-grid', 'form')}
        onSubmit={(event) => {
          event.preventDefault();
          onSearch(currentValues);
        }}
      >
        <table>
          <thead>
            <tr>
              {displayColumns.map(
                (t, index) =>
                  index !== lastColumnIndex && (
                    <FilterBox key={t.field.dataField} width={columnWidth(t.field, index)}>
                      {t.field.filterElement === eFilterType.Text && (
                        <TextInput
                          prependIconType={index === 0 ? IconType.Search : undefined}
                          placeholder={t.field.filterPlaceholder}
                          value={t.data && t.data.filterVal}
                          onChange={(option) =>
                            updateCurrentValues({ value: option || undefined, dataField: t.field.dataField })
                          }
                          data-test-class={dataTestClass || 'data-grid'}
                        />
                      )}
                      {t.field.filterElement === eFilterType.Select && (
                        <SelectFilter
                          column={{ ...t.field, text: t.field.filterPlaceholder }}
                          defaultValue={t.data.filterVal}
                          onFilter={(value) => {
                            updateCurrentValues({ value: value ? value.value : null, dataField: t.field.dataField });
                          }}
                          options={t.field.filterOptions ? t.field.filterOptions : []}
                          data-test-class={dataTestClass || 'data-grid'}
                        />
                      )}
                      {(t.field.filterElement === eFilterType.Number ||
                        t.field.filterElement === eFilterType.Currency) && (
                        <NumberInput
                          thousandSeparator={true}
                          decimalScale={2}
                          mode={t.field.filterElement === eFilterType.Currency ? 'currency' : 'default'}
                          onChange={(option) =>
                            updateCurrentValues({
                              value: option || (option === 0 ? option : undefined),
                              dataField: t.field.dataField,
                            })
                          }
                          placeholder={t.field.filterPlaceholder}
                          value={t.data && t.data.filterVal}
                          valueIsCents={t.field.filterElement === eFilterType.Currency}
                          data-test-class={dataTestClass || 'data-grid'}
                        />
                      )}
                      {t.field.filterElement === eFilterType.DateRange && (
                        <DateRangeFilter
                          defaultValue={t.field.filterDefault}
                          onFilter={(value) => updateCurrentValues({ value: value, dataField: t.field.dataField })}
                          showClear={true}
                          data-test-class={dataTestClass || 'data-grid'}
                        />
                      )}
                    </FilterBox>
                  )
              )}
              <FilterBox width={columnWidth(lastColumn, lastColumnIndex)}>
                <Button
                  intent={'primary'}
                  type="submit"
                  data-test-id={`${dataTestId}-submit-button`}
                  data-test-class={dataTestClass || 'data-grid'}
                >
                  Search
                </Button>
              </FilterBox>
            </tr>
          </thead>
        </table>
      </form>
    </StyledWrapper>
  );
};
